import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import { grey, red, yellow } from '@mui/material/colors';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import ViewIcon from '@mui/icons-material/OpenInBrowser';
import WarningIcon from '@mui/icons-material/Warning';
import { withStyles } from 'tss-react/mui';
import classNames from 'classnames';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { imageProxy } from 'tf-post-parser';
import shadow from '../../helpers/shadow';
import Link from '@mui/material/Link';
import DeleteDraftButton from '../Dashboard/Drafts/DeleteDraftButton';
import UnScheduleButton from '../Dashboard/Drafts/UnScheduleButton';
import Excerpt from './Excerpt';
import PostDeleteBtn from './PostDeleteBtn';

const ChainDetailIcon = dynamic(() => import('./ChainDetailIcon'), {
  ssr: false,
});

dayjs.extend(relativeTime, LocalizedFormat); // use plugin

const styles = theme => ({
  card: {
    borderRadius: 12,
    boxShadow: shadow.md,
  },
  areabg: {
    background: theme.palette.background.light,
  },
  areabgwarn: {
    background: yellow[600],
  },
  areabgunlisted: {
    background: grey[600],
  },
  areahidden: {
    background: red[600],
  },
});
class PostListItem extends Component {
  state = { show: true };

  hide = () => {
    this.setState({ show: false });
  };

  render() {
    const { classes } = this.props;

    // Hide if deleted (for drafts)
    if (!this.state.show) {
      return <></>;
    }
    let button2 = (
      <Link
        color="textPrimary"
        href={`/@${this.props.post.author.username}/${this.props.post.permlink}`}
        className="textPrimary"
      >
        <Button color="inherit" variant="outlined">
          <ViewIcon />
          <span className="tw-hidden sm:tw-block pl-1">View</span>
        </Button>
      </Link>
    );
    if (this.props.isDraftMode) {
      button2 = <DeleteDraftButton id={this.props.id} onDelete={this.hide} />;
      if (this.props.isScheduled)
        button2 = <UnScheduleButton id={this.props.id} onDelete={this.hide} />;
    }
    let colsize = 'col-12';
    if (this.props.post.thumbnail !== undefined) {
      colsize = 'col-xl-8 col-lg-8 col-md-12 col-sm-12 col-12 pl-0';
    }

    let postDate;
    if (this.props.publishedDate) postDate = dayjs(this.props.publishedDate);
    else if (this.props.scheduledDate)
      postDate = dayjs(this.props.scheduledDate);
    let areabg = classNames(classes.areabg);
    let warnText;
    if (
      (this.props.warnWhenHidden &&
        this.props.warnWhenHidden.isBlacklistedFromPost) ||
      this.props.isScheduleFailed
    ) {
      areabg = classNames(classes.areahidden);
      if (this.props.isScheduleFailed)
        warnText =
          'Posting failed. Please try scheduling this post again or posting it manually.';
      else if (this.props.warnWhenHidden.isBlacklistedFromPost)
        warnText =
          'This post was blacklisted after manual review by the TravelFeed content team. It will not appear on TravelFeed. If you believe this is a mistake, please contact us.';
    } else if (
      this.props.warnWhenHidden &&
      this.props.warnWhenHidden.isBlacklistedFromFeed
    ) {
      areabg = classNames(classes.areabgwarn);
      warnText =
        'This post was unlisted by our quality control algorithm. It is still accessible from you profile page and by direct link, but it has reduced visibility in searches. If you believe this is a mistake, please contact us.';
    } else if (
      this.props.warnWhenHidden &&
      this.props.warnWhenHidden.isUnlisted
    ) {
      areabg = classNames(classes.areabgunlisted);
      warnText =
        'You have unlisted this post. It will not appear in searches, feeds or on your profile page, but remains accessible by direct link. Use the three-dot-menu if you want to relist this post.';
    }

    const content = (
      <div className="row">
        {this.props.post.thumbnail !== undefined && (
          <div className="col-lg-4 p-0">
            <CardMedia
              className="h-100"
              style={{ minHeight: '150px' }}
              image={imageProxy(
                this.props.post.thumbnail,
                undefined,
                400,
                'fit',
              )}
            />
          </div>
        )}
        <div className={colsize}>
          <CardContent>
            <div className="pr-2 pl-2 pb-2">
              <Typography gutterBottom variant="h6" component="h2">
                {warnText && (
                  <Tooltip title={warnText} placement="bottom">
                    <div className="pr-2 d-inline">
                      <WarningIcon />
                    </div>
                  </Tooltip>
                )}
                {this.props.isScheduleFailed && (
                  <Tooltip
                    title="This scheduled post could not be posted. This could be a problem with your post. Please check your post and reschedule it."
                    placement="bottom"
                  >
                    <div className="pr-2 d-inline">
                      <WarningIcon />
                    </div>
                  </Tooltip>
                )}
                {this.props.post.title || 'Untitled'}{' '}
                {this.props.snapshot && (
                  <div className="badge badge-success">
                    Snapshot: {this.props.snapshot}
                  </div>
                )}
              </Typography>
              <Excerpt text={this.props.post.excerpt} />
            </div>
          </CardContent>
          <CardActions className={areabg}>
            <div className="tw-ml-2 row tw-w-full">
              <div className="col-8 tw-flex my-auto">
                <span className="textPrimary">
                  {!this.props.isScheduled && (
                    <Link
                      color="textPrimary"
                      href={`/dashboard/publish?${
                        this.props.isDraftMode
                          ? `draftId=${this.props.post.id}${
                              this.props.isPublished ? `&clone=true` : ''
                            }`
                          : `permlink=${encodeURIComponent(
                              this.props.post.permlink,
                            )}`
                      }`}
                    >
                      <Button
                        // className="p-0 pl-2 pr-2"
                        variant="outlined"
                        color="primary"
                      >
                        <EditIcon />
                        <span className="tw-hidden sm:tw-block textPrimary pl-1">
                          {this.props.isPublished ? 'Clone' : 'Edit'}
                        </span>
                      </Button>
                    </Link>
                  )}
                </span>
                {!this.props.isDraftMode && (
                  <PostDeleteBtn
                    onDelete={this.hide}
                    permlink={this.props.post.permlink}
                  />
                )}
                {button2}
              </div>
              <div className="col-4 my-auto text-right pt-1">
                {postDate &&
                  (postDate.isBefore(dayjs().startOf('month').add(-1, 'month'))
                    ? `${
                        this.props.isPublished
                          ? 'Published on'
                          : 'Scheduled for'
                      } ${postDate.format('MMMM YYYY')}`
                    : `${
                        this.props.isPublished ? 'Published ' : 'Scheduled '
                      } ${postDate.fromNow()}`)}
                {
                  // if post is paid out (= older than 7 days), display payout, otherwise display time until payour
                  !this.props.isDraftMode && (
                    <>
                      <ChainDetailIcon
                        chain="hive"
                        author={this.props.post.hiveAuthor}
                        permlink={this.props.post.hivePerm}
                      />
                      <ChainDetailIcon
                        chain="steem"
                        author={this.props.post.steemAuthor}
                        permlink={this.props.post.steemPerm}
                      />
                    </>
                  )
                }
              </div>
            </div>
          </CardActions>
        </div>
      </div>
    );
    return (
      <div className="pt-2 pr-2 pl-2">
        <Card className={classes.card} key={this.props.post.permlink}>
          {content}
        </Card>
      </div>
    );
  }
}

PostListItem.defaultProps = {
  isDraftMode: undefined,
};

PostListItem.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  post: PropTypes.objectOf(PropTypes.any).isRequired,
  isDraftMode: PropTypes.bool,
  id: PropTypes.string.isRequired,
};

export default withStyles(PostListItem, styles, { withTheme: true });
